<template>
  <div class="pagination-container">
    <div class="custom-pagination">
      <div class="pagination-pages">
        <span 
          v-for="(item, index) in displayPages" 
          :key="index" 
          @click="handlePageClick(item)" 
          :class="['page-number', { active: item === current, ellipsis: item === '...' }]">
          {{ item }}
        </span>
      </div>
      
      <div class="pagination-line"></div>

      <div class="pageCurrent">
        <el-input 
          v-model="inputPage" 
          placeholder="请输入页码" 
          class="pagination-input"
          :maxlength="maxInputLength"
          @keyup.enter.native="handleInputJump"
          @blur="handleInputJump"
          :min="1"
          :max="totalPages">
        </el-input>
        <button
          type="button"
          class="jump-btn"
          @click="handleInputJump">
          跳转
        </button>
      </div>
      
      <div class="pagination-buttons">
        <button class="pagination-btn prev-btn" @click="handlePrev" :disabled="current <= 1">
          <img src="../assets/images/left.png" alt="">
        </button>
        
        <button class="pagination-btn next-btn" @click="handleNext" :disabled="current >= totalPages">
          <img src="../assets/images/right.png" alt="">
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Pagination',
  props: {
    total: {
      type: Number,
      required: true,
      default: 0
    },
    pageSize: {
      type: Number,
      default: 10
    },
    currentPage: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      size: this.pageSize,
      current: this.currentPage,
      inputPage: ''
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.size);
    },
    maxInputLength() {
      // 限制输入长度，避免挤压右侧按钮
      return String(this.totalPages || 1).length;
    },
    displayPages() {
      const total = this.totalPages;
      const current = this.current;
      const pages = [];
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
        return pages;
      }
      
      pages.push(1);
      
      if (current > 4) {
        pages.push('...');
      }
      
      let start = Math.max(2, current - 1);
      let end = Math.min(total - 1, current + 1);
      
      if (current <= 4) {
        start = 2;
        end = 4;
      }
      
      if (current >= total - 3) {
        start = total - 3;
        end = total - 1;
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (current < total - 3) {
        pages.push('...');
      }
      
      pages.push(total);
      return pages;
    }
  },
  methods: {
    handlePageClick(page) {
      // 如果点击的是省略号，不执行任何操作
      if (page === '...') {
        return;
      }
      
      if (page !== this.current) {
        this.current = page;
        this.$emit('current-change', page);
      }
    },
    handlePrev() {
      if (this.current > 1) {
        this.current--;
        this.$emit('current-change', this.current);
      }
    },
    handleNext() {
      if (this.current < this.totalPages) {
        this.current++;
        this.$emit('current-change', this.current);
      }
    },
    handleInputJump() {
      const page = parseInt(this.inputPage);
      if (page && page >= 1 && page <= this.totalPages) {
        this.current = page;
        this.$emit('current-change', page);
      } else {
        this.inputPage = '';
      }
    }
  },
  watch: {
    pageSize: {
      handler(val) {
        this.size = val;
      },
      immediate: true
    },
    currentPage: {
      handler(val) {
        this.current = val;
      },
      immediate: true
    }
  }
}
</script>

<style lang="less" scoped>
@import '../assets/styles/variables.less';
@import '../assets/styles/mixins.less';

.pagination-container {
  
  .custom-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    
    .pagination-pages {
      display: flex;
      align-items: center;
      
      .page-number {
        margin: 0 1px;
        min-width: 14px;
        height: 14px;
        line-height: 14px;
        border-radius: 4px;
        font-size: 12px;
        cursor: pointer;
        color: #606266;
        text-align: center;
        
        &:hover {
          color: #19529F;
        }
        
        &.active {
          background-color: #19529F;
          color: white;
        }
        
        &.ellipsis {
          cursor: default;
          min-width: 20px;
          
          &:hover {
            color: #606266;
          }
        }
      }
    }
    
    .pagination-line {
      flex: 1;
      height: 0;
      border-top: 1px solid;
      border-image:  linear-gradient(270deg, rgba(25, 82, 159, 1), rgba(226, 110, 118, 0.42)) 1 1;
      margin: 0 4px;
    }
    
    .pageCurrent {
      display: flex;
      align-items: center;
      background: #FFFFFF;
      border: 1px solid #E4E7ED;
      border-radius: 20px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      height: 23px;
      position: relative;
      
      .pagination-input {
        flex: 0 0 .7rem; // 预留 3 位数空间
        border: none;
        outline: none;
        padding: 0 4px;
        height: 100%;
        background: transparent;
        position: relative;
        
        &::after {
          content: '';
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 60%;
          background: #E4E7ED;
        }
        
        :deep(.el-input__inner) {
          width: .7rem;
          border: none;
          padding: 0 4px;
          height: 100%;
          background: transparent;
          font-size: 10px !important;
          color: #606266;
          text-align: center;
          
          &::placeholder {
            color: #C0C4CC;
          }
        }
      }
      
      .jump-btn {
        padding: 0 4px;
        height: 100%;
        border: none;
        background: transparent;
        color: #19529F;
        font-size: 11px;
        font-weight: 500;
        cursor: pointer;
        white-space: nowrap;
        transition: all 0.3s ease;
        
        &:hover {
          background: rgba(25, 82, 159, 0.08);
        }
        
        &:active {
          background: rgba(25, 82, 159, 0.15);
        }
      }
    }
    
    .pagination-buttons {
      display: flex;
      align-items: center;
      height: 20px;
      
      &::before {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        top: 50%;
        margin-top: -1px;
      }
      
      .pagination-btn {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 20px;
        border-radius: 10px;
        cursor: pointer;
        border: none;
        outline: none;
        margin: 0;
        transition: all 0.3s ease;
        
        img {
          width: 12px;
          height: 8px;
        }
        
        &.prev-btn {
          background: #FFFFFF;
          box-shadow: 0 2px 17px 0 rgba(50, 85, 117, 0.18);
          
          &:hover:not(:disabled) {
            background: #f5f5f5;
          }
          
          &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }
        }
        
        &.next-btn {
          margin-left: 4px;
          background: #19529F;
          box-shadow: 0 2px 17px 2px rgba(60, 92, 167, 0.5);
          color: white;
          
          &:hover:not(:disabled) {
            background: #1a62c0;
          }
          
          &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }
        }
      }
    }
  }
}
</style>